import { useEffect, useState } from 'react'
import { useAppStore } from '../store/app'
import { sendCreateTempAgent, wsRequest } from '../api/ws'
import { tempAgentAvailability } from '../store/tempAgent'
import { getSetupGuideURL } from '../api/config'
import { StarIcon } from '../components/icons'
import './agents/NewAgentWizard.css'

type Step = 'pick' | 'spawning' | 'problem'

export function CreateEntitySheet() {
  const popSheet = useAppStore((s) => s.popSheet)
  const pushSheet = useAppStore((s) => s.pushSheet)
  const pushNav = useAppStore((s) => s.pushNav)
  const hosts = useAppStore((s) => s.hosts)
  const organizationID = useAppStore((s) => s.organizationID)
  const pendingTempSpawn = useAppStore((s) => s.pendingTempSpawn)
  const tempSpawnError = useAppStore((s) => s.tempSpawnError)
  const markPendingTempSpawn = useAppStore((s) => s.markPendingTempSpawn)
  const setTempSpawnError = useAppStore((s) => s.setTempSpawnError)
  const tempAgentDefaults = useAppStore((s) => s.tempAgentDefaults)

  const [step, setStep] = useState<Step>('pick')
  // Tracks whether we've fired the spawn in this sheet session so the
  // completion effect doesn't misfire before spawnTemp() is called.
  const [didSpawn, setDidSpawn] = useState(false)

  // "Describe the problem" flow (Phase 4): hand a free-text problem to the
  // Facilitator, which proposes a hire into the review queue.
  const [problem, setProblem] = useState('')
  const [sending, setSending] = useState(false)
  const [problemError, setProblemError] = useState<string | null>(null)
  const [problemDone, setProblemDone] = useState(false)

  async function askFacilitator() {
    if (!organizationID || !problem.trim() || sending) return
    setSending(true)
    setProblemError(null)
    try {
      const resp = await wsRequest<{ facilitator_tasked?: boolean; error?: string }>(
        'provisioning_propose_from_problem',
        { organization_id: organizationID, problem: problem.trim() },
        'provisioning_problem_proposed',
        { kind: 'organization', id: organizationID, action: 'provisioning_propose_from_problem' },
      )
      if (resp.error === 'no_facilitator') {
        setProblemError(
          'Choose an Onboarding Facilitator in Household Settings first, then try again.',
        )
        setSending(false)
        return
      }
      if (resp.error) {
        setProblemError(resp.error)
        setSending(false)
        return
      }
      setProblemDone(true)
      setSending(false)
    } catch (e) {
      setProblemError(e instanceof Error ? e.message : String(e))
      setSending(false)
    }
  }

  // When spawn in-flight completes (pendingTempSpawn drops to false after
  // we initiated it), close the sheet on success.
  useEffect(() => {
    if (step !== 'spawning' || !didSpawn || pendingTempSpawn) return
    if (tempSpawnError) return
    popSheet()
  }, [step, didSpawn, pendingTempSpawn, tempSpawnError, popSheet])

  function handleChatRoom() {
    popSheet()
    pushSheet({ kind: 'create_chat_room' })
  }

  function handleFullTimeAgent() {
    popSheet()
    pushNav({ kind: 'new_agent_wizard' })
  }

  function handleConnectAccount() {
    popSheet()
    pushSheet({ kind: 'connect_account' })
  }

  // Start from a pattern somebody already worked out (docs/blueprints.md). Sits
  // ABOVE "describe the problem" because it is the more certain path: you are
  // choosing a known role rather than asking an agent to work one out — and it
  // is the only route to a Facilitator in a household that has none, since
  // describing a problem needs one to already exist.
  function handleBlueprint() {
    popSheet()
    pushSheet({ kind: 'blueprint_picker' })
  }

  const hasAnyHost = hosts.length > 0
  const { agentsBlocked, hasDefaults, canSpawnTemp } = tempAgentAvailability(hosts, tempAgentDefaults)
  const tempAgentBlocked = !canSpawnTemp

  function agentNoticeMessage(): string {
    if (!hasAnyHost) return 'No host enrolled yet.'
    if (!hosts.some((h) => h.online)) return 'No host is running — start it with hearth host start.'
    return 'Host is running but no agent harnesses were found.'
  }

  function handleTempAgent() {
    if (tempAgentBlocked || !organizationID || !tempAgentDefaults?.host_id) return
    setDidSpawn(true)
    setStep('spawning')
    markPendingTempSpawn()
    sendCreateTempAgent(organizationID, tempAgentDefaults.host_id)
  }

  function back() {
    if (step === 'problem') { setStep('pick'); return }
    if (step === 'pick' || step === 'spawning') { popSheet(); return }
  }

  const titleFor: Record<Step, string> = {
    pick: 'Create New',
    spawning: 'Starting…',
    problem: 'What do you need?',
  }

  return (
    <div className="wizard wizard--sheet">
      <header className="wizard__nav">
        <button className="wizard__nav-btn" onClick={back}>
          {step === 'pick' ? 'Cancel' : '‹ Back'}
        </button>
        <div className="wizard__nav-title">{titleFor[step]}</div>
        <div className="wizard__nav-spacer" />
      </header>

      <div className="wizard__body">
        {step === 'pick' && (
          <div className="wizard__step">
            <ul className="wizard__list">
              <li>
                <button className="wizard__row" onClick={handleChatRoom}>
                  <span>💬</span>
                  <span>New Chat Room</span>
                </button>
              </li>
              <li>
                <button
                  className="wizard__row"
                  onClick={handleFullTimeAgent}
                  disabled={agentsBlocked}
                >
                  <StarIcon style={{ width: 18, height: 18, verticalAlign: 'middle' }} />
                  <span>Full-time Agent</span>
                </button>
              </li>
              <li>
                <button
                  className="wizard__row"
                  onClick={handleTempAgent}
                  disabled={tempAgentBlocked}
                >
                  <span>⚡</span>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                    <span>Temp Agent</span>
                    {!agentsBlocked && !hasDefaults && (
                      <span className="wizard__row-sub">Set defaults in Household Settings</span>
                    )}
                  </div>
                </button>
              </li>
              <li>
                <button className="wizard__row" onClick={handleConnectAccount}>
                  <span>🔗</span>
                  <span>Connect an Account</span>
                </button>
              </li>
              <li>
                <button
                  className="wizard__row"
                  onClick={handleBlueprint}
                  disabled={agentsBlocked}
                >
                  <span>📐</span>
                  <span>Start from a pattern</span>
                </button>
              </li>
              <li>
                <button className="wizard__row" onClick={() => setStep('problem')}>
                  <span>🧭</span>
                  <span>Not sure? Describe the problem</span>
                </button>
              </li>
            </ul>
            {agentsBlocked && (
              <p className="wizard__notice">
                {agentNoticeMessage()}{' '}
                <a href={getSetupGuideURL()} target="_blank" rel="noopener noreferrer">
                  Get started →
                </a>
              </p>
            )}
          </div>
        )}

        {step === 'spawning' && (
          <div className="wizard__step">
            {tempSpawnError ? (
              <>
                <div className="wizard__error">{tempSpawnError}</div>
                <button
                  className="wizard__primary"
                  onClick={() => {
                    setTempSpawnError(null)
                    setDidSpawn(false)
                    setStep('pick')
                  }}
                >
                  Try Again
                </button>
              </>
            ) : (
              <div className="wizard__hint" style={{ textAlign: 'center', paddingTop: 48 }}>
                ⏳ Starting agent…
              </div>
            )}
          </div>
        )}

        {step === 'problem' && (
          <div className="wizard__step">
            {problemDone ? (
              <>
                <div className="wizard__hint" style={{ textAlign: 'center', paddingTop: 24 }}>
                  On it — I'll suggest something in your review queue shortly.
                </div>
                <button className="wizard__primary" onClick={popSheet}>
                  Done
                </button>
              </>
            ) : (
              <>
                <div className="wizard__hint">
                  Describe what you're trying to do. Your Onboarding Facilitator will
                  suggest whether a new agent — and what it needs — would help, for
                  you to review.
                </div>
                <textarea
                  className="wizard__textarea"
                  value={problem}
                  onChange={(e) => setProblem(e.target.value)}
                  placeholder="e.g. I keep forgetting to water the garden and check the mail."
                  autoFocus
                  disabled={sending}
                />
                {problemError && <div className="wizard__error">{problemError}</div>}
                <button
                  className="wizard__primary"
                  onClick={askFacilitator}
                  disabled={sending || !problem.trim()}
                >
                  {sending ? 'Asking…' : 'Ask'}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
